<template>
    <div ref="chartContainer" class="w-full h-[calc(100%-2rem)] relative">
        <ErrorOverlay :show="errorShown" icon="error_outline" message="情報の取得中にエラーが発生しました<br>後でもう一度お試しください" />
    </div>
  </template>
  
  <script lang="ts">
import { defineComponent, ref, onMounted, watch, nextTick, PropType, onUnmounted } from 'vue';
import vegaEmbed, { VisualizationSpec } from 'vega-embed';
import { VitalChartAggrData } from '../../models/vitalChartAggrData';
import ErrorOverlay from '../../common/ErrorOverlay.vue';

  export default defineComponent({
    name: 'ChartComponent',
    components: {
        ErrorOverlay
    },
    props: {
        chartData: {
            type: Array as PropType<VitalChartAggrData[]>,
            required: true
        },
        showErrorOverlay: {
            type: Boolean,
            default: false
        },
        legendsOrientRight: {
            type: Boolean,
            default: true
        },
    },
    setup(props, { emit }) {
        const chartContainer = ref<HTMLElement | null>(null);
        const errorShown = ref(props.showErrorOverlay);
        
        // 1. Add a persistent selection tracker
        const persistSelectedBar = ref<number | null>(null);
        
        // Add these variables at the setup level
        let resizeTimeout: number | null = null;
        let resizeObserver: ResizeObserver | null = null;
        
        const renderChart = () => {
            if (props.showErrorOverlay || !chartContainer.value || !props.chartData.length) return;

            chartContainer.value.innerHTML = '';

            // Vega spec configuration...
            const vegaSpec: VisualizationSpec = {
                "$schema": "https://vega.github.io/schema/vega/v5.json",
                "width": chartContainer.value.clientWidth - (props.legendsOrientRight ? 200 : 50),
                "height": chartContainer.value.clientHeight - (props.legendsOrientRight ? 70 : 120),
                "data": [
                    {
                        "name": "table",
                        "values": props.chartData,
                        "transform": [
                        {
                                "type": "formula",
                                "as": "heatzone_category",
                                "expr": "datum.max_heat_strain && datum.max_heat_strain.value != null ? " +
                                    "datum.max_heat_strain.value < 1 ? 1 : " +
                                    "datum.max_heat_strain.value < 3 ? 2 : " +
                                    "datum.max_heat_strain.value < 7 ? 3 : 4 : null"
                            },
                            {
                                "type": "formula",
                                "as": "max_hsi",
                                "expr": "datum.max_heat_strain ? datum.max_heat_strain.value : null"
                            },
                            {
                                "type": "formula",
                                "as": "body_temp",
                                "expr": "datum.avg_body_temp ? datum.avg_body_temp.value : null"
                            },
                            {
                                "type": "formula",
                                "as": "skin_temp",
                                "expr": "datum.avg_skin_temp ? datum.avg_skin_temp.value : null"
                            },
                            {
                                "type": "formula",
                                "as": "heart_rate",
                                "expr": "datum.avg_heart_rate ? datum.avg_heart_rate.value : null"
                            }
                        ]
                    },
                    {
                        "name": "selectedBarsData",
                        "values": []
                    },
                    {
                        "name": "draggingBars",
                        "values": []
                    }
                ],
                
                "scales": [
                    {
                        "name": "xscale",
                        "type": "band",
                        "domain": { "data": "table", "field": "key" },
                        "range": "width",
                        "padding": 0.1,
                        "round": true
                    },
                    {
                        "name": "tempScale",
                        "type": "linear",
                        "domain": [30, 42],
                        "nice": true,
                        "range": "height",
                        "zero": false
                    },
                    {
                        "name": "heartRateScale",
                        "type": "linear",
                        "domain": [60, 140],
                        "nice": true,
                        "range": "height",
                        "zero": false
                    },
                    {
                        "name": "colorScale",
                        "type": "ordinal",
                        "domain": [null, 1, 2, 3, 4],
                        "range": [
                            "#D3D3D3",  // grey for null
                            "#90C590",  // soft green for zone 1 (hsi < 1)
                            "#E6D690",  // soft yellow for zone 2 (1 ≤ hsi < 3)
                            "#E6A67C",  // soft orange for zone 3 (3 ≤ hsi < 7)
                            "#E68A8A"   // soft red for zone 4 (hsi ≥ 7)
                        ]
                    },
                    {
                        "name": "lineType",
                        "type": "ordinal",
                        "domain": ["深部体温", "皮膚温度", "心拍数"],
                        "range": ["#006400", "#3CB371", "#FF0000"]
                    }
                ],
                
                "axes": [
                    {
                        "orient": "bottom",
                        "scale": "xscale",
                        "format": "%H:%M",
                        "formatType": "time",
                        "titleFontSize": 14,
                        "labelFlush": true,
                        "labelOverlap": true
                    },
                    {
                        "orient": "left",
                        "scale": "tempScale",
                        "title": "（℃）",
                        "titleAnchor": "end",
                        "titleAlign": "left",
                        "titleY": -12,
                        "titleAngle": 0
                    },
                    {
                        "orient": "right",
                        "scale": "heartRateScale",
                        "title": "（拍/分）",
                        "titleAnchor": "end",
                        "titleY": -12,
                        "titleAlign": "right",
                        "titleAngle": 0
                    }
                ],
                
                "legends": [
                    {
                        "stroke": "lineType",
                        "orient": props.legendsOrientRight ? "right" : "top",
                        "direction": props.legendsOrientRight ? "vertical" : "horizontal",
                        "title": "凡例",
                        "symbolType": "circle",
                        "symbolFillColor": "white",
                        "symbolStrokeWidth": 1.5,
                    },
                    {
                        "fill": "colorScale",
                        "orient": props.legendsOrientRight ? "right" : "bottom",
                        "direction": props.legendsOrientRight ? "vertical" : "horizontal",
                        "title": "暑熱負荷インデックス",
                        "symbolType": "square",
                        "symbolSize": 100,
                        "columns": props.legendsOrientRight ? 1 : 3,
                        "encode": {
                            "labels": {
                                "update": {
                                    "text": {
                                        "signal": "datum.value === null ? 'データなし' : " +
                                            "datum.value === 1 ? 'ゾーン1 (HSI < 1)' : " +
                                            "datum.value === 2 ? 'ゾーン2 (1 ≤ HSI < 3)' : " +
                                            "datum.value === 3 ? 'ゾーン3 (3 ≤ HSI < 7)' : " +
                                            "'ゾーン4 (HSI ≥ 7)'"
                                    }
                                }
                            }
                        }
                    }
                ],

                "signals": [
                    {
                        "name": "isMouseDown",
                        "value": false,
                        "on": [
                            {
                                "events": { "source": "view", "type": "mousedown" },
                                "update": "true"
                            },
                            {
                                "events": { "source": "view", "type": "mouseup" },
                                "update": "false"
                            }
                        ]
                    },
                    {
                        "name": "draggingBars",
                        "value": []
                    },
                    {
                        "name": "brush",
                        "value": 0,
                        "on": [
                            {
                                "events": { "source": "view", "type": "mousedown" },
                                "update": "inrange(x(), [0, width]) && inrange(y(), [0, height]) ? {x: x(), y: y(), extent_x: 0, extent_y: 0} : 0"
                            },
                            {
                                "events": {
                                    "source": "window", "type": "mousemove", "consume": true,
                                    "between": [{ "source": "view", "type": "mousedown" },
                                    { "source": "window", "type": "mouseup" }]
                                },
                                "update": "brush.x === 0 ? 0 : " +
                                    "{" +
                                    "x: brush.x, " +
                                    "y: brush.y, " +
                                    "extent_x: clamp(x(), 0, width) - brush.x, " +
                                    "extent_y: 0" +
                                    "}"
                            },
                            {
                                "events": { "source": "view", "type": "mouseup" },
                                "update": "brush.x === 0 ? 0 : " +
                                    "{" +
                                    "x: brush.x, " +
                                    "y: brush.y, " +
                                    "extent_x: clamp(x(), 0, width) - brush.x, " +
                                    "extent_y: 0" +
                                    "}"
                            }
                        ]
                    },
                    {
                        "name": "xSelectionThreshold",
                        "value": 10
                    },
                    {
                        "name": "hoveredBar",
                        "value": null,
                        "on": [
                            {
                                "events": "rect:mouseover",
                                "update": "isMouseDown ? hoveredBar : datum"
                            },
                            {
                                "events": "rect:mouseout",
                                "update": "null"
                            }
                        ]
                    },
                    {
                        "name": "selectedBar",
                        "value": null
                    },
                    {
                        "name": "selectedBars",
                        "value": []
                    },
                    {
                        "name": "animationPercentage",
                        "value": 1
                    },
                    {
                        "name": "dragStartBar",
                        "value": null
                    },
                    {
                        "name": "selectionRange",
                        "value": { "start": null, "end": null }
                    },
                    {
                        "name": "dragExtension",
                        "value": 2 // Small extension during drag
                    }
                ],
                
                "marks": [
                    {
                        "type": "rect",
                        "from": { "data": "table" },
                        "encode": {
                            "enter": {
                                "x": { "scale": "xscale", "field": "key" },
                                "width": { "scale": "xscale", "band": 1 },
                                "y": { "value": 0 },
                                "y2": { "signal": "height" },
                                "fill": { "scale": "colorScale", "field": "heatzone_category" },
                                "tooltip": {
                                    "signal": "{" +
                                        "'開始時間': timeFormat(datum.key-500000, '%Y年%m月%d日%H時%M分'), " +
                                        "'終了時間': timeFormat(datum.key, '%Y年%m月%d日%H時%M分'), " +
                                        "'最高HSI': datum.max_hsi === null ? 'データなし' : format(datum.max_hsi, '.1f'), " +
                                        "'平均深部体温': datum.body_temp === null ? 'データなし' : format(datum.body_temp, '.1f') + '℃', " +
                                        "'平均皮膚温度': datum.skin_temp === null ? 'データなし' : format(datum.skin_temp, '.1f') + '℃', " +
                                        "'平均心拍数': datum.heart_rate === null ? 'データなし' : format(datum.heart_rate, '.0f') + '拍/分'" +
                                    "}"
                                }
                            },
                            "update": {
                                "fill": [
                                    {
                                        "test": "indata('draggingBars', 'value', datum.key)",
                                        "signal": "scale('colorScale', datum.heatzone_category) === '#90C590' ? '#A8E0A8' : " +
                                            "scale('colorScale', datum.heatzone_category) === '#E6D690' ? '#FFF0A0' : " +
                                            "scale('colorScale', datum.heatzone_category) === '#E6A67C' ? '#FFBD8E' : " +
                                            "scale('colorScale', datum.heatzone_category) === '#E68A8A' ? '#FF9E9E' : " +
                                            "scale('colorScale', datum.heatzone_category)"
                                    },
                                    {
                                        "test": "datum === hoveredBar && datum.heatzone_category !== null && !isMouseDown",
                                        "signal": "scale('colorScale', datum.heatzone_category) === '#90C590' ? '#A8E0A8' : " +
                                            "scale('colorScale', datum.heatzone_category) === '#E6D690' ? '#FFF0A0' : " +
                                            "scale('colorScale', datum.heatzone_category) === '#E6A67C' ? '#FFBD8E' : " +
                                            "scale('colorScale', datum.heatzone_category) === '#E68A8A' ? '#FF9E9E' : " +
                                            "scale('colorScale', datum.heatzone_category)"
                                    },
                                    { "scale": "colorScale", "field": "heatzone_category" }
                                ], 
                                "y": {
                                    "signal": "selectedBar === datum.key || indata('selectedBarsData', 'value', datum.key) ? " +
                                        "-5 * animationPercentage : " +
                                        "indata('draggingBars', 'value', datum.key) ? -dragExtension : 0"
                                },
                                "y2": { "signal": "height" },
                                "fillOpacity": { "value": 0.8 }
                            }
                        }
                    },
                    {
                        "type": "group",
                        "clip": true,
                        "marks": [
                    {
                        "type": "line",
                                "from": { "data": "table" },
                        "encode": {
                            "enter": {
                                        "x": { "scale": "xscale", "field": "key", "band": 0.5 },
                                        "y": { "scale": "tempScale", "field": "body_temp" },
                                        "stroke": { "value": "#006400" },
                                        "strokeWidth": { "value": 1 },
                                        "defined": { "signal": "datum.body_temp != null" }
                            }
                        }
                    },
                    {
                        "type": "symbol",
                                "from": { "data": "table" },
                        "encode": {
                            "enter": {
                                        "x": { "scale": "xscale", "field": "key", "band": 0.5 },
                                        "y": { "scale": "tempScale", "field": "body_temp" },
                                        "size": { "value": 15 },
                                        "fill": { "value": "white" },
                                        "stroke": { "value": "#006400" },
                                        "strokeWidth": { "value": 1 },
                                        "opacity": { "signal": "datum.body_temp == null ? 0 : 1" }
                            }
                        }
                    },
                    {
                        "type": "line",
                                "from": { "data": "table" },
                        "encode": {
                            "enter": {
                                        "x": { "scale": "xscale", "field": "key", "band": 0.5 },
                                        "y": { "scale": "tempScale", "field": "skin_temp" },
                                        "stroke": { "value": "#3CB371" },
                                        "strokeWidth": { "value": 1 },
                                        "defined": { "signal": "datum.skin_temp != null" }
                            }
                        }
                    },
                    {
                        "type": "symbol",
                                "from": { "data": "table" },
                        "encode": {
                            "enter": {
                                        "x": { "scale": "xscale", "field": "key", "band": 0.5 },
                                        "y": { "scale": "tempScale", "field": "skin_temp" },
                                        "size": { "value": 15 },
                                        "fill": { "value": "white" },
                                        "stroke": { "value": "#3CB371" },
                                        "strokeWidth": { "value": 1 },
                                        "opacity": { "signal": "datum.skin_temp == null ? 0 : 1" }
                            }
                        }
                    },
                    {
                        "type": "line",
                                "from": { "data": "table" },
                                "encode": {
                                    "enter": {
                                        "x": { "scale": "xscale", "field": "key", "band": 0.5 },
                                        "y": { "scale": "heartRateScale", "field": "heart_rate" },
                                        "stroke": { "value": "#FF0000" },
                                        "strokeWidth": { "value": 1 },
                                        "defined": { "signal": "datum.heart_rate != null" }
                                    }
                                }
                            },
                            {
                                "type": "symbol",
                                "from": { "data": "table" },
                                "encode": {
                                    "enter": {
                                        "x": { "scale": "xscale", "field": "key", "band": 0.5 },
                                        "y": { "scale": "heartRateScale", "field": "heart_rate" },
                                        "size": { "value": 15 },
                                        "fill": { "value": "white" },
                                        "stroke": { "value": "#FF0000" },
                                        "strokeWidth": { "value": 1 },
                                        "opacity": { "signal": "datum.heart_rate == null ? 0 : 1" }
                                    }
                                }
                            }
                        ]
                    },
                    {
                        "type": "group",
                        "marks": [
                            // Upper bound indicators with correct null handling
                            {
                                "type": "symbol",
                                "from": { "data": "table" },
                                "encode": {
                                    "enter": {
                                        "x": {
                                            "scale": "xscale",
                                            "field": "key",
                                            "band": 0.5,
                                            "offset": {
                                                "signal": "datum.body_temp > 42 ? " +
                                                    "(datum.heart_rate > 140 && datum.skin_temp > 42 ? -3 : " +
                                                    "(datum.heart_rate > 140 || datum.skin_temp > 42) ? -2 : 0) : 0"
                                            }
                                        },
                                        "y": { "value": 0 },
                                        "shape": { "value": "triangle-up" },
                                        "size": { "value": 100 },
                                        "fill": { "value": "#006400" },
                                        "opacity": [
                                            { "test": "datum.body_temp != null && datum.body_temp > 42", "value": 1 },
                                            { "value": 0 }
                                        ]
                                    }
                                }
                            },
                            {
                                "type": "symbol",
                                "from": { "data": "table" },
                                "encode": {
                                    "enter": {
                                        "x": {
                                            "scale": "xscale",
                                            "field": "key",
                                            "band": 0.5,
                                            "offset": {
                                                "signal": "datum.heart_rate > 140 ? " +
                                                    "(datum.body_temp > 42 && datum.skin_temp > 42 ? 0 : " +
                                                    "datum.body_temp > 42 ? 2 : " +
                                                    "datum.skin_temp > 42 ? -2 : 0) : 0"
                                            }
                                        },
                                        "y": { "value": 0 },
                                        "shape": { "value": "triangle-up" },
                                        "size": { "value": 100 },
                                        "fill": { "value": "#FF0000" },
                                        "opacity": [
                                            { "test": "datum.heart_rate != null && datum.heart_rate > 140", "value": 1 },
                                            { "value": 0 }
                                        ]
                                    }
                                }
                            },
                            {
                                "type": "symbol",
                                "from": { "data": "table" },
                                "encode": {
                                    "enter": {
                                        "x": {
                                            "scale": "xscale",
                                            "field": "key",
                                            "band": 0.5,
                                            "offset": {
                                                "signal": "datum.skin_temp > 42 ? " +
                                                    "(datum.body_temp > 42 && datum.heart_rate > 140 ? 3 : " +
                                                    "datum.body_temp > 42 ? 2 : " +
                                                    "datum.heart_rate > 140 ? 2 : 0) : 0"
                                            }
                                        },
                                        "y": { "value": 0 },
                                        "shape": { "value": "triangle-up" },
                                        "size": { "value": 100 },
                                        "fill": { "value": "#3CB371" },
                                        "opacity": [
                                            { "test": "datum.skin_temp != null && datum.skin_temp > 42", "value": 1 },
                                            { "value": 0 }
                                        ]
                                    }
                                }
                            },
                            // Lower bound indicators with correct stacking priority
                            {
                                "type": "symbol",
                                "from": { "data": "table" },
                                "encode": {
                                    "enter": {
                                        "x": {
                                            "scale": "xscale",
                                            "field": "key",
                                            "band": 0.5,
                                            "offset": {
                                                "signal": "datum.body_temp < 30 ? -3 : 0"
                                            }
                                        },
                                        "y": { "signal": "height" },
                                        "shape": { "value": "triangle-down" },
                                        "size": { "value": 100 },
                                        "fill": { "value": "#006400" },
                                        "opacity": [
                                            { "test": "datum.body_temp != null && datum.body_temp < 30", "value": 1 },
                                            { "value": 0 }
                                        ]
                                    }
                                }
                            },
                            {
                                "type": "symbol",
                                "from": { "data": "table" },
                                "encode": {
                                    "enter": {
                                        "x": {
                                            "scale": "xscale",
                                            "field": "key",
                                            "band": 0.5,
                                            "offset": {
                                                "signal": "datum.heart_rate < 60 ? " +
                                                    "(datum.body_temp < 30 && datum.skin_temp < 30 ? 0 : " +
                                                    "datum.body_temp < 30 ? 2 : " +
                                                    "datum.skin_temp < 30 ? -2 : 0) : 0"
                                            }
                                        },
                                        "y": { "signal": "height" },
                                        "shape": { "value": "triangle-down" },
                                        "size": { "value": 100 },
                                        "fill": { "value": "#FF0000" },
                                        "opacity": [
                                            { "test": "datum.heart_rate != null && datum.heart_rate < 60", "value": 1 },
                                            { "value": 0 }
                                        ]
                                    }
                                }
                            },
                            {
                                "type": "symbol",
                                "from": { "data": "table" },
                                "encode": {
                                    "enter": {
                                        "x": {
                                            "scale": "xscale",
                                            "field": "key",
                                            "band": 0.5,
                                            "offset": {
                                                "signal": "datum.skin_temp < 30 ? " +
                                                    "(datum.body_temp < 30 && datum.heart_rate < 60 ? 3 : " +
                                                    "datum.body_temp < 30 ? 2 : " +
                                                    "datum.heart_rate < 60 ? 2 : 0) : 0"
                                            }
                                        },
                                        "y": { "signal": "height" },
                                        "shape": { "value": "triangle-down" },
                                        "size": { "value": 100 },
                                        "fill": { "value": "#3CB371" },
                                        "opacity": [
                                            { "test": "datum.skin_temp != null && datum.skin_temp < 30", "value": 1 },
                                            { "value": 0 }
                                        ]
                                    }
                                }
                            }
                        ]
                    },
                    {
                        "type": "rule",
                        "encode": {
                            "enter": {
                                "y": { "scale": "tempScale", "value": 37.5 },
                                "x": { "value": 0 },
                                "x2": { "signal": "width" },
                                "stroke": { "value": "#4CAF50" },
                                "strokeWidth": { "value": 1 }
                            }
                        }
                    },
                    {
                        "type": "rect",
                        "name": "brushArea",
                        "encode": {
                            "enter": {
                                "fill": { "value": "#333" },
                                "fillOpacity": { "value": 0 }
                            },
                            "update": {
                                "x": { "signal": "brush.extent_x < 0 ? brush.x + brush.extent_x : brush.x" },
                                "y": { "value": 0 },
                                "width": { "signal": "abs(brush.extent_x)" },
                                "height": { "signal": "height" },
                                "stroke": { "value": "#666" },
                                "strokeWidth": { "value": 0 },
                                "opacity": { "value": 0 }
                            }
                        }
                    }
                ]
            };
            
            vegaEmbed(chartContainer.value, vegaSpec, {
                renderer: 'canvas',
                actions: false
            }).then(result => {
                const view = result.view;
                // 3. Add a watcher to reapply selection when chart rerenders
                watch(() => props.chartData, () => {
                    // Restore selection on data change if we have a selected bar
                    if (persistSelectedBar.value !== null) {
                        nextTick(() => {
                            if (view) {
                                view.signal('selectedBar', persistSelectedBar.value);
                                view.data('selectedBarsData', []);
                                view.runAsync();
                            }
                        });
                    }
                }, { deep: true });

                // Clean up mousedown listener
                view.addSignalListener('isMouseDown', (name, value) => {
                    if (value === true) {
                        // Reset drag state on new mouse down
                        view.signal('dragStartBar', null);
                        view.data('draggingBars', []);
                    }
                });

                // Clean up brush listener
                view.addSignalListener('brush', (name, value) => {
                    if (!value) return;
                    
                    const allData = props.chartData;
                    const scaleValue = view.scale('xscale');
                    
                    if (view.signal('isMouseDown')) {
                        // Find starting bar on first drag
                        if (view.signal('dragStartBar') === null) {
                            const startBar = findBarAtPosition(value.x, allData, scaleValue);
                            if (startBar) {
                                view.signal('dragStartBar', startBar.key);
                            }
                        }
                        
                        // Find current bar under cursor
                        const currentBar = findBarAtPosition(value.x + value.extent_x, allData, scaleValue);
                        
                        if (!currentBar || view.signal('dragStartBar') === null) {
                            setTimeout(() => view.runAsync(), 0);
                            return;
                        }
                        
                        const startKey = view.signal('dragStartBar');
                        const endKey = currentBar.key;
                        
                        // Update selection
                        if (startKey === endKey) {
                            view.data('draggingBars', [{ value: startKey }]);
                        } else {
                            const minKey = Math.min(startKey, endKey);
                            const maxKey = Math.max(startKey, endKey);
                            
                            const selectedBars = allData.filter(d => d.key >= minKey && d.key <= maxKey);
                            view.data('draggingBars', selectedBars.map(d => ({ value: d.key })));
                        }
                        
                        // Schedule update
                        setTimeout(() => view.runAsync(), 0);
                    } else {
                        // Mouse released - finalize selection
                        const draggedBars = view.data('draggingBars');
                        
                        // Clear drag state
                        view.data('draggingBars', []);
                        view.signal('dragStartBar', null);
                        
                        // Process selection if we have bars
                        if (draggedBars.length === 1) {
                            emit('bar-click', draggedBars[0].value);
                            animateBars(view, draggedBars[0].value, null);
                        }

                        if (draggedBars.length > 1) {
                            const selectedBarKeys = draggedBars.map((d: any) => d.value);
                            const selectedBars = allData.filter(d => selectedBarKeys.includes(d.key));
                            
                            if (selectedBars.length > 0) {
                                selectedBars.sort((a, b) => a.key - b.key);
                                const startTimestamp = selectedBars[0].key;
                                const endTimestamp = selectedBars[selectedBars.length - 1].key;
                                
                                emit('bars-selected', { startTimestamp, endTimestamp });
                                animateBars(view, null, selectedBars.map(d => d.key));
                            } else {
                                setTimeout(() => view.runAsync(), 0);
                            }
                        } else {
                            setTimeout(() => view.runAsync(), 0);
                        }
                    }
                });
            })
            // .catch((error: any) => {
            //     // console.error('Error rendering chart:', error)
            // });
        };

        // Single watcher for both data changes and resize
        watch([() => props.chartData, () => chartContainer.value?.clientWidth], async () => {
            await nextTick();
            renderChart();
        }, { deep: true });
        
        onMounted(async () => {
            await nextTick();
            renderChart();

            // Create resize observer with debounce to prevent loops
            if (chartContainer.value) {
                resizeObserver = new ResizeObserver(() => {
                    // Debounce the resize handler
                    if (resizeTimeout) {
                        window.clearTimeout(resizeTimeout);
                    }

                    resizeTimeout = window.setTimeout(() => {
                        renderChart();
                        resizeTimeout = null;
                    }, 10);
                });

                resizeObserver.observe(chartContainer.value);
            }
        });
        
        // Separate onUnmounted hook at the same level as onMounted
        onUnmounted(() => {
            cleanupVegaTooltips();
            
            if (resizeTimeout) {
                window.clearTimeout(resizeTimeout);
            }

            if (resizeObserver) {
                if (chartContainer.value) {
                    resizeObserver.unobserve(chartContainer.value);
                }
                resizeObserver.disconnect();
            }
        });

        // 4. Modify animation function to retain selection
        function animateBars(view: any, singleBar: any, multipleBars: any) {
            // First set the animation to 0 (bars in normal position)
            view.signal('animationPercentage', 0);
            view.signal('brush', 0);
            
            // Set the target selections
            if (singleBar !== null) {
                view.signal('selectedBar', singleBar);
                view.data('selectedBarsData', []);
                persistSelectedBar.value = singleBar; // Store to persistent ref
            } else if (multipleBars !== null) {
                view.signal('selectedBar', null);
                view.data('selectedBarsData', multipleBars.map((key: any) => ({ value: key })));
                persistSelectedBar.value = null; // Clear persistent ref
            } else {
                view.signal('selectedBar', null);
                view.data('selectedBarsData', []);
                persistSelectedBar.value = null; // Clear persistent ref
            }
            
            // Run once to apply initial state
            view.runAsync().then(() => {
                // Start animation after initial state is applied
                const startTime = Date.now();
                const duration = 200;
                
                function step() {
                    const elapsed = Date.now() - startTime;
                    const progress = Math.min(elapsed / duration, 1);
                    
                    view.signal('animationPercentage', progress);
                    
                    // Use runAsync for safer updates
                    view.runAsync().then(() => {
                        if (progress < 1) {
                            requestAnimationFrame(step);
                        }
                    });
                }
                
                requestAnimationFrame(step);
            });
        }
        
        // Helper function to find a bar at a specific x-position
        function findBarAtPosition(x: number, allData: VitalChartAggrData[], scaleValue: any) {
            if (!scaleValue) return null;
            
            return allData.find(d => {
                const barX = scaleValue(d.key);
                if (barX === undefined) return false;
                
                const barWidth = scaleValue.bandwidth();
                return x >= barX && x <= (barX + barWidth);
            });
        }
        
        const cleanupVegaTooltips = () => {
            const tooltips = document.querySelectorAll('.vg-tooltip');
            tooltips.forEach(tooltip => {
                if (tooltip.parentNode) {
                    tooltip.parentNode.removeChild(tooltip);
                }
            });
        };

        // Add this watch effect to update errorShown when the prop changes
        watch(() => props.showErrorOverlay, (newValue) => {
            errorShown.value = newValue;
        });

        return {
            chartContainer,
            renderChart,
            errorShown,
            props,
            cleanupVegaTooltips
        }
    }
  });
  </script>